import { ReactElement, useCallback } from "react";
import { HubCommentSection, HubCommentSectionService } from "client/Features/Hub/HubCommentSection/HubCommentSection";
import { useCommentSection } from "client/Features/Hub/HubCommentSection/hooks/useCommentSection";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { HubCommentModel, HubCommentPagingInformationModel } from "client/Api/Hub/common";
import { SortDirection } from "client/Api/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { BusyIndicator, NoResults } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./HubItemCommentSection.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";

export type HubItemCommentSectionProps = {
	kind: HubItemKind;
	timezone: TimeZone;
	options: HubCommentOptionsModel;
	allCommentsCount: number;
	service: HubCommentSectionService;
	onStatisticsUpdate: () => void;
	onMobileCommentIsEditingChanged: (isEditing: boolean) => void;
};

export const HubItemCommentSection = (props: HubItemCommentSectionProps): ReactElement => {
	const {
		kind,
		timezone,
		options,
		allCommentsCount,
		service,
		onStatisticsUpdate,
		onMobileCommentIsEditingChanged
	} = props;

	const getCommentsListHandler = useCallback(
		(
			pageSize: number,
			after: HubCommentPagingInformationModel | null,
			sortDirection: SortDirection,
			signal: AbortSignal
		): Promise<LazyLoadPageResult<HubCommentModel>> => {
			return service.getCommentsList(
				pageSize,
				after,
				sortDirection,
				signal
			);
		},
		[service]
	);

	const {
		commentListRef,
		currentUserInfo,
		imageViewerDialog,
		isMobile,
		mobileCommentFormRef,
		state: commentState,
		structuredComments,
		mobileMode,
		sortPreference,
		loadMoreHandler,
		initMobileMode,
		mobileModeChangeHandler,
		onSortPreferenceChange,
		commentDeletedHandler,
		commentReactionHandler,
		commentCreatedHandler,
		commentUpdatedHandler,
		viewImageHandler,
		reloadHandler
	} = useCommentSection({
		getCommentsList: getCommentsListHandler,
		sortKey: hubItemKindToSortKey[kind],
		onStatisticsUpdate
	});

	const hasComments: boolean = structuredComments.length > 0;

	if (commentState.status === "loading" && !hasComments) {
		return (
			<div className={styles.busyIndicatorContainer}>
				<BusyIndicator />
			</div>
		);
	}

	if (commentState.status === "error" && !hasComments) {
		return (
			<NoResults
				icon="gear"
				title={localize("_NotAbleToRetrieveComments")}
				message={localize("_TheCommentsCouldNotBeRetrieved")}
				onRetry={reloadHandler}
			/>
		);
	}

	return (
		<>
			<HubCommentSection>
				{!isMobile && (
					<HubCommentSection.CommentForm
						service={service}
						onCreated={commentCreatedHandler}
						currentUserInfo={currentUserInfo}
						options={options}
					/>
				)}
				{hasComments && (
					<>
						<HubCommentSection.Header
							total={allCommentsCount}
							sortPreference={sortPreference}
							onSortPreferenceChange={onSortPreferenceChange}
						/>
						<HubCommentSection.CommentList
							ref={commentListRef}
							comments={structuredComments}
							onStatisticsUpdated={commentReactionHandler}
							onUpdated={commentUpdatedHandler}
							onDeleted={commentDeletedHandler}
							onCreated={commentCreatedHandler}
							isMobile={isMobile}
							mobileModeChangeHandler={mobileModeChangeHandler}
							mobileMode={mobileMode}
							service={service}
							currentUserInfo={currentUserInfo}
							timezone={timezone}
							viewImageHandler={viewImageHandler}
							commentOptions={options}
							renderAfterItems={
								commentState.status === "error"
									? (
										<NoResults
											icon="gear"
											title={localize("_NotAbleToRetrieveComments")}
											message={localize("_TheCommentsCouldNotBeRetrieved")}
											onRetry={loadMoreHandler}
										/>
									)
									: (
										<HubCommentSection.LoadMore
											variant="load-more-indicator"
											hasMore={commentState.hasMore}
											isLoading={commentState.status === "loading"}
											onLoadMore={loadMoreHandler}
										/>
									)
							}
						/>
					</>
				)}
				{isMobile && (
					<HubCommentSection.MobileComment
						currentUserInfo={currentUserInfo}
						onCreated={commentCreatedHandler}
						mode={mobileMode}
						onCancel={initMobileMode}
						onUpdated={commentUpdatedHandler}
						ref={mobileCommentFormRef}
						onMobileCommentIsEditingChanged={onMobileCommentIsEditingChanged}
						service={service}
						options={options}
					/>
				)}
			</HubCommentSection>
			{imageViewerDialog}
		</>
	);
};
HubItemCommentSection.displayName = "HubItemCommentSection";

const hubItemKindToSortKey: { [key in HubItemKind]: string } = {
	[HubItemKind.Announcement]: "announcement:commentsSort",
	[HubItemKind.Event]: "event:commentsSort",
	[HubItemKind.KnowledgeItem]: "knowledgeItem:commentsSort"
};